// Click-to-preview dialog shared by the composer's pending attachment tiles
// and sent message attachments.

import type { ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/shared/ui/dialog';
import type { FilePreview } from '../model/useFilePreview';

export function PreviewTable({ rows, className }: { rows: string[][]; className?: string }) {
  return (
    <table className={cn('w-full table-fixed border-collapse', className)}>
      <tbody>
        {rows.map((row, r) => (
          <tr key={r}>
            {row.map((cell, c) => (
              <td key={c} className="truncate border border-border/60 px-1">
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Only wraps the trigger when there's a real preview to show full-size; a
// plain-icon tile renders its children unwrapped.
export function AttachmentPreviewDialog({
  preview,
  name,
  children,
}: {
  preview: FilePreview;
  name?: string;
  children: ReactNode;
}) {
  if (preview.kind === 'none') return children;
  return (
    <Dialog>
      <DialogTrigger asChild className="cursor-zoom-in">
        {children}
      </DialogTrigger>
      <DialogContent className="max-w-3xl p-2">
        <DialogTitle className="sr-only">Attachment preview</DialogTitle>
        {preview.kind === 'image' && (
          <img src={preview.src} alt={name ?? ''} className="block h-auto max-h-[80vh] w-auto max-w-full rounded-sm object-contain" />
        )}
        {preview.kind === 'pdf' && (
          <embed src={preview.src} type="application/pdf" className="h-[80vh] w-full rounded-sm" />
        )}
        {preview.kind === 'table' && <PreviewTable rows={preview.rows} className="text-sm" />}
        {preview.kind === 'text' && (
          <pre className="max-h-[80vh] overflow-auto rounded-sm bg-secondary p-3 text-xs whitespace-pre-wrap">
            {preview.snippet}
          </pre>
        )}
      </DialogContent>
    </Dialog>
  );
}
